import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Pagination,
  Skeleton,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import useDocumentInvoiceData, {
  LineItem,
} from '@/hooks/useDocumentInvoiceData'
import { getMaxDecimals } from '@/utils/decimal'
import { formatValue } from '@/components/purchase-order-view/shared'

const LINE_ITEMS_PAGE_SIZE = 20
const PAGINATION_THRESHOLD = 20

const HEADER_CELL_SX: SxProps = {
  fontWeight: 600,
  whiteSpace: 'nowrap',
}

type SummaryField = { label: string; value: string | null }

function FieldSlot({ label, value }: SummaryField) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Tooltip title={value || ''} disableHoverListener={!value}>
        <Typography variant="body2" noWrap>
          {value || '—'}
        </Typography>
      </Tooltip>
    </Box>
  )
}

function SummarySkeleton() {
  return (
    <Card elevation={4} sx={{ pointerEvents: 'all' }}>
      <CardContent>
        <Skeleton
          variant="text"
          width={160}
          sx={{ fontSize: '1.25rem', mb: 2 }}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 2,
            mb: 3,
          }}
        >
          {[0, 1, 2, 3, 4].map((field) => (
            <Skeleton key={field} variant="text" width="70%" />
          ))}
        </Box>
        <Stack spacing={1.25}>
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} variant="text" width="100%" />
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

type LineItemsTableProps = {
  lineItems: LineItem[]
  columnVisibility: {
    description: boolean
    job: boolean
    phase: boolean
    glAccount: boolean
    costType: boolean
    quantity: boolean
    rate: boolean
    amount: boolean
  }
}

function SummaryLineItemsTable({
  lineItems,
  columnVisibility,
}: LineItemsTableProps) {
  const [page, setPage] = useState(1)

  const showPager = lineItems.length >= PAGINATION_THRESHOLD
  const pageCount = Math.max(
    1,
    Math.ceil(lineItems.length / LINE_ITEMS_PAGE_SIZE),
  )

  // Row values are invalidated live, so `lineItems` can shrink out from under
  // whatever page the user is sitting on — snap back into range rather than
  // rendering an empty page or a nonsensical "Showing" range.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount)
  }, [page, pageCount])

  const currentPage = Math.min(page, pageCount)
  const pagedLineItems = lineItems.slice(
    (currentPage - 1) * LINE_ITEMS_PAGE_SIZE,
    currentPage * LINE_ITEMS_PAGE_SIZE,
  )

  const qtyDecimals = getMaxDecimals(lineItems.map((li) => li.quantity))
  const rateDecimals = Math.max(
    2,
    getMaxDecimals(lineItems.map((li) => li.rate)),
  )
  const amountDecimals = Math.max(
    2,
    getMaxDecimals(lineItems.map((li) => li.amount)),
  )

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columnVisibility.description && (
                <TableCell sx={HEADER_CELL_SX}>Description</TableCell>
              )}
              {columnVisibility.job && (
                <TableCell sx={HEADER_CELL_SX}>Job</TableCell>
              )}
              {columnVisibility.phase && (
                <TableCell sx={HEADER_CELL_SX}>Phase</TableCell>
              )}
              {columnVisibility.glAccount && (
                <TableCell sx={HEADER_CELL_SX}>GL Account</TableCell>
              )}
              {columnVisibility.costType && (
                <TableCell sx={HEADER_CELL_SX}>Cost Type</TableCell>
              )}
              {columnVisibility.quantity && (
                <TableCell sx={HEADER_CELL_SX} align="right">
                  Qty
                </TableCell>
              )}
              {columnVisibility.rate && (
                <TableCell sx={HEADER_CELL_SX} align="right">
                  Rate
                </TableCell>
              )}
              {columnVisibility.amount && (
                <TableCell sx={HEADER_CELL_SX} align="right">
                  Amount
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedLineItems.map((li) => (
              <TableRow key={li.rowId}>
                {columnVisibility.description && (
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      title={li.description ?? undefined}
                    >
                      {li.description ?? '—'}
                    </Typography>
                  </TableCell>
                )}
                {columnVisibility.job && (
                  <TableCell title={li.jobDisplayName}>
                    {li.jobNumber || '—'}
                  </TableCell>
                )}
                {columnVisibility.phase && (
                  <TableCell title={li.phaseDisplayName}>
                    {li.phaseNumber || '—'}
                  </TableCell>
                )}
                {columnVisibility.glAccount && (
                  <TableCell title={li.glAccountDisplayName}>
                    {li.glAccountNumber || '—'}
                  </TableCell>
                )}
                {columnVisibility.costType && (
                  <TableCell title={li.costTypeDisplayName}>
                    {li.costTypeNumber || '—'}
                  </TableCell>
                )}
                {columnVisibility.quantity && (
                  <TableCell align="right">
                    {formatValue(li.quantity, { decimals: qtyDecimals })}
                  </TableCell>
                )}
                {columnVisibility.rate && (
                  <TableCell align="right">
                    {formatValue(li.rate, {
                      decimals: rateDecimals,
                      prefix: '$',
                    })}
                  </TableCell>
                )}
                {columnVisibility.amount && (
                  <TableCell align="right">
                    {formatValue(li.amount, {
                      decimals: amountDecimals,
                      prefix: '$',
                    })}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showPager && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {(currentPage - 1) * LINE_ITEMS_PAGE_SIZE + 1}–
            {Math.min(currentPage * LINE_ITEMS_PAGE_SIZE, lineItems.length)} of{' '}
            {lineItems.length}
          </Typography>
          <Pagination
            size="small"
            count={pageCount}
            page={currentPage}
            onChange={(_event, newPage) => setPage(newPage)}
          />
        </Stack>
      )}
    </>
  )
}

export default function DocumentApprovalSummaryCard() {
  const {
    isReady,
    vendorName,
    invoiceNumber,
    invoiceDate,
    purchaseOrderNumber,
    dueDate,
    invoiceTotal,
    lineItems,
    availability,
  } = useDocumentInvoiceData()

  if (!isReady) {
    return <SummarySkeleton />
  }

  const fields: SummaryField[] = [
    availability.vendor && { label: 'Vendor', value: vendorName },
    availability.invoiceNumber && {
      label: 'Invoice Number',
      value: invoiceNumber,
    },
    availability.invoiceDate && { label: 'Invoice Date', value: invoiceDate },
    availability.poNumber && {
      label: 'Purchase Order',
      value: purchaseOrderNumber,
    },
    availability.dueDate && { label: 'Due Date', value: dueDate },
  ].filter((field): field is SummaryField => !!field)

  const invoiceTotalDecimals = Math.max(2, getMaxDecimals([invoiceTotal]))

  return (
    <Card elevation={4} sx={{ pointerEvents: 'all' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Document Summary
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 2,
            mb: 2,
          }}
        >
          {fields.map((field) => (
            <FieldSlot key={field.label} {...field} />
          ))}
        </Box>

        {lineItems.length === 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              textAlign: 'center',
              py: 2,
              borderTop: 1,
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2,
            }}
          >
            No line items have been extracted from this invoice
          </Typography>
        ) : (
          <SummaryLineItemsTable
            lineItems={lineItems}
            columnVisibility={{
              description: availability.description,
              job: availability.job,
              phase: availability.phase,
              glAccount: availability.glAccount,
              costType: availability.costType,
              quantity: availability.quantity,
              rate: availability.rate,
              amount: availability.amount,
            }}
          />
        )}

        {availability.invoiceTotal && (
          // pr matches the table cells' right padding so the total value
          // right-aligns with the Amount column.
          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ mt: 2, pr: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Total{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1rem',
                  ml: 1,
                }}
              >
                {formatValue(invoiceTotal, {
                  decimals: invoiceTotalDecimals,
                  prefix: '$',
                })}
              </Box>
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}
